// ============================================================================
// Centro de Aplicaciones — la cáscara de app.dimoauto.com
//
// Rediseño de agosto de 2026: misma estructura de siempre (barra superior con
// los cuatro destinos + portada con el Asesor), repintada con el sistema de
// diseño de la suite (`src/ui`). El botón «Marketing» ya no abre el hub viejo:
// abre la app SEO/GEO, que reúne toda la información de marketing.
//
// Las apps embebidas reciben `?embed=1`: con esa marca esconden su propio
// logotipo y su botón de salir, porque de la marca y de la sesión se encarga
// esta cáscara. Así no se ven dos logotipos ni dos «Cerrar sesión».
// ============================================================================
import React, { useState } from 'react';
import Login from './components/auth/Login';
import Asesor from './components/chat/Asesor';
import { C, GRAD } from './ui/theme.js';
import { NavIcon } from './ui/Sidebar.jsx';
import { leerSesion, borrarSesion } from './ui/sesion.js';

// ==================== DESTINOS ====================
// Los dos destinos de trabajo. Van centrados en la barra superior y son las dos
// tarjetas grandes de la portada.
const DESTINOS = [
  {
    id: 'marketing',
    title: 'Marketing',
    icon: 'marketing',
    description: 'SEO, visibilidad en IA, tráfico web, campañas y negocio local',
    // 13-ago: era 'seo.dimoauto.com', que NO tiene registro DNS — el boton
    // Marketing daba "no se ha podido encontrar la direccion IP del servidor".
    // El subdominio que si resuelve al Hetzner (204.168.215.119) es este.
    url: 'https://marketing.dimoauto.com',
  },
  {
    id: 'crm',
    title: 'CRM',
    icon: 'admin',
    description: 'Entrada de leads, gestión comercial y administración',
    url: 'https://crmadmin.dimoauto.com',
  },
];

// Servidores queda fuera de la barra superior: no es una herramienta del día a
// día, es la ficha de cómo va la máquina. Vive solo en la portada, en pequeño y
// centrado bajo las dos tarjetas grandes.
const SERVIDORES = {
  id: 'servidores',
  title: 'Servidores',
  icon: 'costes',
  description: 'Consumo de servicios y alertas de presupuesto',
  url: 'https://costes.dimoauto.com',
};

// Dimo Brain (alta del 13-ago): el mapa del conocimiento del grupo —procesos,
// notas y cómo se conecta cada pieza—. Va con Servidores en la fila pequeña de
// la portada y no como tercera tarjeta grande, porque las dos grandes son los
// dos sitios donde se trabaja a diario (Marketing y CRM) y meter una tercera
// rompía esa lectura. Si se acaba usando cada día, sube a tarjeta grande sin
// más que moverla al array DESTINOS.
/* La URL de Dimo Brain: la que le asignó Coolify, porque `brain.dimoauto.com`
   todavía no tiene registro DNS.

   Va en http:// (Coolify no emite certificado para los sslip.io). Si el Centro
   de Aplicaciones se sirve por https, el navegador NO deja meter una página
   http dentro de un iframe —contenido mixto— y el hueco se quedaría en blanco
   sin decir por qué. De eso se encarga `SinPublicar`: detecta el caso y ofrece
   abrirla en una pestaña, en vez de dejar la pantalla muerta.

   Lo definitivo es crear el registro A `brain` → 204.168.215.119 y darlo de
   alta en Coolify: con https propio, la app se embebe como las demás. */
const BRAIN_URL = 'http://ldb65zj7phom0t2w1v29xwaf.204.168.215.119.sslip.io/';

const BRAIN = {
  id: 'brain',
  title: 'Dimo Brain',
  icon: 'brain',
  description: 'El mapa del conocimiento del grupo: procesos, notas y conexiones',
  url: BRAIN_URL,
};

// Llamadas en directo (alta del 4-nov): prueba de llamada de seguimiento de
// taller con relevo IA → supervisor. Necesita https para usar el micrófono; si
// la URL es http, `SinPublicar` ofrece abrirla en una pestaña.
const LLAMADAS_URL = 'http://t7tkyz543dhf2i0qzswxez5g.204.168.215.119.sslip.io/';

const LLAMADAS = {
  id: 'llamadas',
  title: 'Llamadas en directo',
  icon: 'callcenter',
  description: 'Seguimiento de taller con la IA y relevo al supervisor',
  url: LLAMADAS_URL,
};

// Los accesos pequeños de la portada, en el orden en que se pintan.
const SECUNDARIOS = [SERVIDORES, BRAIN, LLAMADAS];

const TODOS = [...DESTINOS, ...SECUNDARIOS];

// Añade la marca de embebido conservando la query que ya lleve la URL.
const conEmbed = (url) => (url.includes('?') ? `${url}&embed=1` : `${url}?embed=1`);

// ==================== BARRA SUPERIOR ====================

function BotonNav({ destino, activo, onClick }) {
  return (
    <button onClick={onClick} className="fg-nav" title={destino.description} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 10,
      border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
      whiteSpace: 'nowrap', flexShrink: 0,
      background: activo ? GRAD : 'transparent',
      color: activo ? '#fff' : C.page,
      boxShadow: activo ? '0 6px 16px rgba(0,81,135,.28)' : 'none',
    }}>
      <NavIcon name={destino.icon} size={16} />
      {destino.corto || destino.title}
    </button>
  );
}

function BarraSuperior({ activo, onNavegar, user, onLogout }) {
  const inicial = (user?.nombre || user?.email || '?').trim()[0]?.toUpperCase() || '?';
  return (
    <header style={{
      height: 56, flexShrink: 0, background: '#fff', borderBottom: `1px solid ${C.line}`,
      display: 'flex', alignItems: 'center', gap: 16, padding: '0 16px', position: 'relative', zIndex: 100,
    }}>
      <button onClick={() => onNavegar('home')} title="Ir a la portada" style={{
        border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, flexShrink: 0,
        display: 'flex', alignItems: 'center', height: 34,
      }}>
        <img src="/logotipo.png" alt="Dimoauto" style={{ height: 24, width: 'auto', display: 'block' }} />
      </button>

      <div style={{ width: 1, height: 24, background: C.line, flexShrink: 0 }} />

      {/* Inicio a la izquierda. */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
        <BotonNav
          destino={{ id: 'home', title: 'Centro de Aplicaciones', corto: 'Inicio', icon: 'centro', description: 'Portada del grupo, con el asesor de datos' }}
          activo={activo === 'home'}
          onClick={() => onNavegar('home')}
        />
      </div>

      {/* Marketing y CRM: centrados sobre el eje de la página, no sobre el
          hueco que dejan el logotipo y el usuario. Por eso van en posición
          absoluta al 50 % en vez de repartir el ancho con flex — así caen
          exactamente encima del logotipo y de las tarjetas de la portada,
          midan lo que midan los bloques de los lados. */}
      <nav className="ca-nav-centro" style={{
        display: 'flex', gap: 4, position: 'absolute', left: '50%', top: 0, height: '100%',
        transform: 'translateX(-50%)', alignItems: 'center',
      }}>
        {DESTINOS.map((d) => (
          <BotonNav key={d.id} destino={d} activo={activo === d.id} onClick={() => onNavegar(d.id)} />
        ))}
      </nav>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%', background: GRAD, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 800,
        }}>{inicial}</div>
        <div style={{ lineHeight: 1.25, minWidth: 0 }} className="ca-usuario">
          <div style={{ fontSize: 11.5, fontWeight: 700, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
            {user?.nombre || user?.email || 'Usuario'}
          </div>
          <div style={{ fontSize: 10, color: C.faint }}>{user?.rol || 'usuario'}</div>
        </div>
        <button onClick={onLogout} style={{
          fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, color: C.page, cursor: 'pointer',
          border: `1px solid ${C.line}`, borderRadius: 8, padding: '7px 12px', background: '#fff',
          whiteSpace: 'nowrap',
        }}>Salir</button>
      </div>
    </header>
  );
}

// ==================== PORTADA ====================

function AccesoRapido({ destino, onClick }) {
  return (
    <button onClick={onClick} className="fg-card" style={{
      display: 'flex', alignItems: 'center', gap: 11, textAlign: 'left', cursor: 'pointer',
      fontFamily: 'inherit', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12,
      padding: '13px 16px', width: 268, maxWidth: '100%',
    }}>
      <span style={{
        width: 34, height: 34, borderRadius: 10, background: `${C.brand}12`, color: C.brand,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <NavIcon name={destino.icon} size={17} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: C.ink }}>{destino.title}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: C.muted, lineHeight: 1.4, marginTop: 2 }}>{destino.description}</span>
      </span>
    </button>
  );
}

function Portada({ onNavegar }) {
  return (
    <div className="fg-fade" style={{
      flex: 1, overflowY: 'auto', background: '#fff',
      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '44px 24px 24px',
    }}>
      <img src="/logotipo.png" alt="Dimoauto" style={{ width: 340, maxWidth: '78%', height: 'auto', marginBottom: 26 }} />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 12, maxWidth: 560 }}>
        {DESTINOS.map((d) => (
          <AccesoRapido key={d.id} destino={d} onClick={() => onNavegar(d.id)} />
        ))}
      </div>

      {/* Servidores y Dimo Brain: mismo acceso, en pequeño y centrado. Se entra
          de vez en cuando, no todos los días, y el tamaño lo dice sin necesidad
          de explicarlo. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 30 }}>
        {SECUNDARIOS.map((d) => (
          <button key={d.id} onClick={() => onNavegar(d.id)} className="fg-nav" title={d.description} style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'inherit',
            background: 'transparent', border: `1px solid ${C.line}`, borderRadius: 999,
            padding: '7px 16px', color: C.muted, fontSize: 12, fontWeight: 700,
          }}>
            <NavIcon name={d.icon} size={14} />
            {d.title}
          </button>
        ))}
      </div>

      <Asesor />

      <footer style={{ padding: 20, textAlign: 'center', color: C.faint, fontSize: 12 }}>
        Grupo Dimoauto © {new Date().getFullYear()}
      </footer>
    </div>
  );
}

/* ¿El navegador va a bloquear este iframe por contenido mixto? Pasa cuando la
   cáscara se sirve por https y el destino solo tiene http. */
function bloqueadoPorMixto(url) {
  if (typeof window === 'undefined') return false;
  return window.location.protocol === 'https:' && /^http:\/\//i.test(url);
}

/* Panel para un destino sin publicar, o publicado en una dirección que el
   navegador no deja embeber. Dice qué pasa y ofrece la salida que hay. */
function SinPublicar({ destino, onVolver }) {
  const mixto = destino.url && bloqueadoPorMixto(destino.url);
  return (
    <div className="fg-fade" style={{
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#fff', padding: '40px 24px',
    }}>
      <div style={{ maxWidth: 470, textAlign: 'center' }}>
        <span style={{
          width: 54, height: 54, borderRadius: 16, background: `${C.brand}12`, color: C.brand,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
        }}>
          <NavIcon name={destino.icon} size={26} />
        </span>
        <h2 style={{ fontSize: 19, fontWeight: 800, color: C.ink, margin: '0 0 8px', letterSpacing: -0.2 }}>
          {mixto ? `${destino.title} se abre en una pestaña` : `${destino.title} todavía no está publicado`}
        </h2>
        <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, margin: '0 0 20px' }}>
          {mixto
            ? 'La app está en marcha, pero su dirección de Coolify va por http y esta pantalla por https: el navegador no deja meter una dentro de la otra. Se abre en su propia pestaña hasta que tenga dominio propio.'
            : 'La app existe, pero aún no tiene una dirección a la que apuntar: no hay ningún subdominio del grupo respondiendo por ella. Por eso el acceso está aquí, listo, pero apagado.'}
        </p>

        {mixto && (
          <a href={destino.url} target="_blank" rel="noreferrer" className="fg-nav" style={{
            display: 'inline-block', marginBottom: 20, fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
            color: '#fff', background: C.brand, borderRadius: 10, padding: '12px 26px', textDecoration: 'none',
            boxShadow: '0 3px 12px rgba(0,81,135,.28)',
          }}>Abrir {destino.title} ↗</a>
        )}
        <div style={{
          textAlign: 'left', background: '#f8fafc', border: `1px solid ${C.line}`,
          borderRadius: 12, padding: '14px 16px', fontSize: 12.5, color: C.page, lineHeight: 1.65,
        }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, color: C.muted, textTransform: 'uppercase', marginBottom: 8 }}>
            {mixto ? 'Para verla aquí dentro' : 'Qué falta'}
          </div>
          {mixto ? (
            <>
              <div style={{ marginBottom: 6 }}>1 · Crear el registro DNS <b>brain</b> → <b>204.168.215.119</b>.</div>
              <div style={{ marginBottom: 6 }}>2 · Ponerle ese dominio en Coolify, que le emite el certificado solo.</div>
              <div>3 · Cambiar <code style={{ background: '#eef2f6', borderRadius: 4, padding: '1px 6px' }}>BRAIN_URL</code> a <code style={{ background: '#eef2f6', borderRadius: 4, padding: '1px 6px' }}>https://brain.dimoauto.com</code>. Nada más.</div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 6 }}>1 · Desplegar la app en Coolify.</div>
              <div style={{ marginBottom: 6 }}>2 · Copiar la dirección que le asigne Coolify.</div>
              <div>3 · Pegarla en <code style={{ background: '#eef2f6', borderRadius: 4, padding: '1px 6px' }}>BRAIN_URL</code> de <code style={{ background: '#eef2f6', borderRadius: 4, padding: '1px 6px' }}>src/App.jsx</code>. Nada más.</div>
            </>
          )}
        </div>
        <button onClick={onVolver} className="fg-nav" style={{
          marginTop: 20, fontFamily: 'inherit', fontSize: 13, fontWeight: 800, color: '#fff',
          background: C.brand, border: 'none', borderRadius: 10, padding: '11px 22px', cursor: 'pointer',
          boxShadow: '0 3px 12px rgba(0,81,135,.28)',
        }}>Volver a la portada</button>
      </div>
    </div>
  );
}

// ==================== APP PRINCIPAL ====================
function App() {
  // Si ya hay sesión compartida (cookie de *.dimoauto.com) se entra directo:
  // ni al recargar ni al volver de otra app del grupo hay que teclear nada.
  const [user, setUser] = useState(() => {
    const s = leerSesion();
    return s ? { email: s.email, nombre: s.nombre, rol: s.rol } : null;
  });
  const [vista, setVista] = useState('home');

  /* Navegar a un destino. Si es uno que el navegador no deja embeber —hoy Dimo
     Brain, que va por http mientras esta cáscara va por https— se abre
     directamente en su pestaña y no se cambia de vista (petición 13-ago): antes
     había que pasar por una pantalla intermedia para dar un segundo clic, y
     para eso ya está el propio botón. La pantalla explicativa se queda solo
     para los destinos que aún no tienen dirección ninguna. */
  const navegar = (id) => {
    const d = TODOS.find((x) => x.id === id);
    if (d && d.url && bloqueadoPorMixto(d.url)) {
      window.open(d.url, '_blank', 'noopener');
      return;
    }
    setVista(id);
  };

  if (!user) return <Login onLogin={setUser} />;

  const salir = () => {
    borrarSesion();
    localStorage.removeItem('centro-remember-password');
    setUser(null);
    setVista('home');
  };

  const destino = TODOS.find((d) => d.id === vista);

  const contenido = () => {
    // Destino dado de alta pero todavía sin publicar, o publicado en http
    // mientras esta cáscara va por https: el navegador bloquearía el iframe y
    // solo se vería un hueco blanco. En ambos casos, panel explicativo.
    if (destino && !destino.url) {
      return <SinPublicar destino={destino} onVolver={() => setVista('home')} />;
    }

    if (destino && destino.url) {
      return (
        <iframe
          key={destino.id}
          src={conEmbed(destino.url)}
          title={destino.title}
          style={{ flex: 1, width: '100%', border: 'none', display: 'block' }}
          allow="fullscreen; clipboard-read; clipboard-write; microphone; autoplay"
        />
      );
    }
    return <Portada onNavegar={navegar} />;
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: C.bg }}>
      <BarraSuperior activo={vista} onNavegar={navegar} user={user} onLogout={salir} />
      <main style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {contenido()}
      </main>
    </div>
  );
}

export default App;
